/**
 * Hook returning the org-resolved booking-link base URL.
 *
 * The backend stores the configured share-base URL in `RuntimeConfig` so
 * each environment (qa / staging / prod) can use its own dedicated booking
 * domain (e.g. `https://meet.ahoy.ai`) without rebuilding the frontend.
 * This hook fetches the resolved value from
 * `GET /api/orgs/<orgId>/scheduling/share-config/` once per org and caches
 * it across the page lifetime via React Query.
 *
 * Callers that need a synchronous value (e.g. inside a click handler that
 * runs before the query has resolved) should fall back to
 * `window.location.origin`. The hook returns `null` while loading and on
 * error, making "explicit fallback" the obvious code path.
 *
 * Usage::
 *
 *   const { baseUrl } = useBookingLinkBaseUrl();
 *   const url = `${baseUrl ?? window.location.origin}/m/${shortCode}/`;
 */

import { useQuery } from '@tanstack/react-query';

import { apiGet } from '@/lib/http/client';

interface ShareConfigResponse {
  share_base_url: string;
}

interface UseBookingLinkBaseUrlResult {
  /** Resolved base URL (no trailing slash), or null while loading / on error. */
  baseUrl: string | null;
  isLoading: boolean;
}

export function useBookingLinkBaseUrl(
  orgId: number | null | undefined
): UseBookingLinkBaseUrlResult {
  const { data, isLoading } = useQuery({
    queryKey: ['scheduling-share-config', orgId],
    queryFn: () =>
      apiGet<ShareConfigResponse>(
        `/api/orgs/${orgId}/scheduling/share-config/`
      ),
    enabled: typeof orgId === 'number',
    // The booking-link domain is set once per environment via RuntimeConfig
    // and effectively never changes within an end-user's session. Treat the
    // value as immortal: fetched once on first use, then served from cache
    // for the rest of the page lifetime. Users who toggle envs (rare — devs
    // only) get the new value on next reload.
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    baseUrl: data?.share_base_url ?? null,
    isLoading,
  };
}
